import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms';
import { Input } from '../atoms';
import { Select } from '../atoms';
import { Heading } from '../atoms/Typography/Heading';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Checkbox } from '../atoms/Checkbox';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import type { LivestockRecord, CreateLivestockInput, UpdateLivestockInput } from '../../services/livestockService';
import { useCreateLivestock, useUpdateLivestock } from '../../hooks/useLivestock';
import './LivestockForm.css';
import { Alert } from '../atoms/Alert/Alert';
import { LivestockMaturityNotice } from '../maturity/LivestockMaturityNotice';
import { LivestockPhotosField } from './LivestockPhotosField';
import { dedupeLeadingGenus } from './LivestockIdentifyFlow/scientificName';
import { deceasedDateError, localToday } from './livestockDeceased';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { careSummary, lookupLivestockName, type LivestockNameMatch } from '../../services/livestockIdentificationService';

export interface LivestockFormProps {
  aquariumId: string;
  record?: LivestockRecord | null;
  prefill?: LivestockFormPrefill | null;
  /** Overrides for flows that reuse this form (e.g. the photo identification step). */
  copy?: LivestockFormCopy;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Render only the form, without its own Dialog, for flows that already show a dialog
   * (e.g. the photo identification flow). A dialog must never be nested inside another
   * dialog: the inner overlay gets clipped inside the outer dialog's scrolling body.
   */
  embedded?: boolean;
  /**
   * Look the common name up once when the form opens (photo identification, "name it
   * yourself": the user already confirmed the name, so the details are fetched for them).
   */
  autoLookup?: boolean;
  /**
   * Called after a successful save. The form stays disabled until a returned promise
   * settles (e.g. the photo upload after the record was created), so a second click
   * can never create a second record. A create passes the new record's id and whether the
   * user kept the identification photo, so a caller can link it — existing callers that
   * ignore the arguments are unaffected.
   */
  onSuccess: (created?: { id: string }, options?: LivestockFormSuccessOptions) => void | Promise<void>;
}

/** What a create reports back: the photo choice and the names/category as saved. */
export interface LivestockFormSuccessOptions {
  attachPhoto: boolean;
  saved?: {
    commonName: string;
    scientificName: string | null;
    category: 'coral' | 'fish' | 'invertebrate' | 'other';
  };
}

export interface LivestockFormCopy {
  title?: string;
  intro?: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  saveError?: string;
  attachPhotoLabel?: string;
  attachPhotoHint?: string;
}

export interface LivestockFormPrefill {
  commonName?: string;
  scientificName?: string;
  category?: 'coral' | 'fish' | 'invertebrate' | 'other';
  notes?: string;
  tempPhotoKey?: string;
}

interface FormState {
  commonName: string;
  scientificName: string;
  category: 'coral' | 'fish' | 'invertebrate' | 'other' | '';
  status: 'active' | 'archived' | 'deceased' | 'quarantine' | '';
  dateAdded: string;
  purchaseDate: string;
  purchasedFrom: string;
  purchasePrice: string;
  citesNumber: string;
  notes: string;
  /** Date of death; only used (and shown) while status is 'deceased'. */
  deceasedAt: string;
  deceasedReason: string;
}

interface FormErrors {
  commonName?: string;
  category?: string;
  status?: string;
  dateAdded?: string;
  purchasePrice?: string;
  deceasedAt?: string;
  general?: string;
}

const EMPTY_FORM: FormState = {
  commonName: '',
  scientificName: '',
  category: '',
  status: 'active',
  dateAdded: '',
  purchaseDate: '',
  purchasedFrom: '',
  purchasePrice: '',
  citesNumber: '',
  notes: '',
  deceasedAt: '',
  deceasedReason: '',
};

function recordToFormState(record: LivestockRecord): FormState {
  return {
    commonName: record.commonName,
    scientificName: record.scientificName ?? '',
    category: record.category,
    status: record.status,
    dateAdded: record.dateAdded,
    purchaseDate: record.purchaseDate ?? '',
    purchasedFrom: record.purchasedFrom ?? '',
    purchasePrice: record.purchasePrice != null ? String(record.purchasePrice) : '',
    citesNumber: record.citesNumber ?? '',
    notes: record.notes ?? '',
    deceasedAt: record.deceasedAt ?? '',
    deceasedReason: record.deceasedReason ?? '',
  };
}

function prefillToFormState(prefill: LivestockFormPrefill): FormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    ...EMPTY_FORM,
    commonName: prefill.commonName ?? '',
    scientificName: dedupeLeadingGenus(prefill.scientificName ?? ''),
    category: prefill.category ?? '',
    dateAdded: today,
    notes: prefill.notes ?? '',
  };
}

type LookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'found'; match: LivestockNameMatch }
  | { status: 'none' }
  | { status: 'error'; error: unknown };

/** A typed scientific name the lookup may replace: empty, the common name again, or just the genus. */
function replaceableScientificName(current: string, commonName: string, suggested: string): boolean {
  const c = current.trim().toLowerCase();
  if (!c || c === commonName.trim().toLowerCase()) return true;
  const genus = suggested.split(' ')[0]?.toLowerCase();
  return c === genus || c === `${genus} sp.`;
}

function validateForm(form: FormState, t: (key: string) => string, wasDeceasedWithoutDate = false): FormErrors {
  const errors: FormErrors = {};
  if (!form.commonName.trim()) errors.commonName = t('livestock.validation.commonNameRequired');
  if (!form.category) errors.category = t('livestock.validation.categoryRequired');
  if (!form.status) errors.status = t('livestock.validation.statusRequired');
  if (!form.dateAdded) errors.dateAdded = t('livestock.validation.dateAddedRequired');
  if (form.purchasePrice !== '' && isNaN(parseFloat(form.purchasePrice))) {
    errors.purchasePrice = t('livestock.validation.purchasePriceInvalid');
  }
  // A date of death is optional only for animals marked deceased before dates were recorded.
  if (form.status === 'deceased' && (form.deceasedAt || !wasDeceasedWithoutDate)) {
    const problem = deceasedDateError(form.deceasedAt, form.dateAdded);
    if (problem) errors.deceasedAt = t(problem);
  }
  return errors;
}

export function LivestockForm({
  aquariumId,
  record,
  prefill,
  copy,
  isOpen,
  onClose,
  embedded = false,
  autoLookup = false,
  onSuccess,
}: LivestockFormProps) {
  const { t } = useTranslation();
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const deceasedDateRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [attachPhoto, setAttachPhoto] = useState<boolean>(true);

  const createMutation = useCreateLivestock(aquariumId);
  const updateMutation = useUpdateLivestock(aquariumId, record?.id ?? '');

  const isEdit = record != null;
  // From the click until the caller's onSuccess has finished (photo upload included).
  const [finishing, setFinishing] = useState(false);
  const submittingRef = useRef(false);
  const isSubmitting = createMutation.isPending || updateMutation.isPending || finishing;
  const [lookup, setLookup] = useState<LookupState>({ status: 'idle' });
  const autoLookupDone = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (record) {
        setForm(recordToFormState(record));
      } else if (prefill) {
        setForm(prefillToFormState(prefill));
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setForm({ ...EMPTY_FORM, dateAdded: today });
      }
      setErrors({});
      setAttachPhoto(true);
      setLookup({ status: 'idle' });
      autoLookupDone.current = false;
    }
  }, [isOpen, record, prefill]);

  /** Looks the common name up and fills what is still open: scientific name, category, a care line in the notes. */
  const runLookup = useCallback(
    async (name: string, category: FormState['category']) => {
      if (name.trim().length < 2) return;
      setLookup({ status: 'loading' });
      try {
        const match = await lookupLivestockName(aquariumId, name.trim(), category || null);
        if (!match) {
          setLookup({ status: 'none' });
          return;
        }
        setLookup({ status: 'found', match });
        const care = careSummary(match, t);
        setForm((prev) => {
          const scientific =
            match.scientificName && replaceableScientificName(prev.scientificName, prev.commonName, match.scientificName)
              ? match.scientificName
              : prev.scientificName;
          const careLine = care ? t('livestock.lookup.careNote', { care }) : '';
          const notes = careLine && !prev.notes.includes(careLine) ? [prev.notes.trim(), careLine].filter(Boolean).join('\n') : prev.notes;
          return { ...prev, scientificName: scientific, category: prev.category || match.category || '', notes };
        });
      } catch (error) {
        setLookup({ status: 'error', error });
      }
    },
    [aquariumId, t],
  );

  useEffect(() => {
    if (!isOpen || !autoLookup || autoLookupDone.current || !form.commonName.trim()) return;
    autoLookupDone.current = true;
    void runLookup(form.commonName, form.category);
  }, [isOpen, autoLookup, form.commonName, form.category, runLookup]);

  // Embedded as a later step of another dialog, the button that led here has just unmounted:
  // move focus to the first field so keyboard and screen-reader users land in the form.
  useEffect(() => {
    if (embedded && isOpen) firstFieldRef.current?.focus();
  }, [embedded, isOpen]);

  const handleChange = useCallback(
    (field: keyof FormState) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        setForm(prev => ({
          ...prev,
          [field]: value,
          // Switching to "deceased" proposes today as the date of death.
          ...(field === 'status' && value === 'deceased' && !prev.deceasedAt ? { deceasedAt: localToday() } : {}),
        }));
        setErrors(prev => {
          const next = { ...prev };
          delete next[field as keyof FormErrors];
          return next;
        });
      },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // Synchronous guard: a second click before React re-renders the disabled button is ignored.
      if (submittingRef.current) return;
      const validationErrors = validateForm(
        form,
        t,
        record?.status === 'deceased' && !record.deceasedAt,
      );
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      const category = form.category as 'coral' | 'fish' | 'invertebrate' | 'other';
      const status = form.status as 'active' | 'archived' | 'deceased' | 'quarantine';
      const payload: CreateLivestockInput | UpdateLivestockInput = {
        commonName: form.commonName.trim(),
        // Never "Zebrasoma Zebrasoma flavescens" (the API applies the same guard).
        scientificName: dedupeLeadingGenus(form.scientificName.trim()) || null,
        category,
        status,
        dateAdded: form.dateAdded,
        purchaseDate: form.purchaseDate || null,
        purchasedFrom: form.purchasedFrom.trim() || null,
        purchasePrice: form.purchasePrice !== '' ? parseFloat(form.purchasePrice) : null,
        citesNumber: form.citesNumber.trim() || null,
        notes: form.notes.trim() || null,
        // Date of death + reason only for deceased animals; the API clears them otherwise.
        ...(status === 'deceased'
          ? { deceasedAt: form.deceasedAt || null, deceasedReason: form.deceasedReason.trim() || null }
          : {}),
      };

      submittingRef.current = true;
      setFinishing(true);
      try {
        if (isEdit) {
          await updateMutation.mutateAsync(payload as UpdateLivestockInput);
          await onSuccess();
        } else {
          const created = await createMutation.mutateAsync(payload as CreateLivestockInput);
          // Linking the identification photo is the caller's job (it owns the temp upload).
          await onSuccess(created?.id ? { id: created.id } : undefined, {
            attachPhoto: Boolean(prefill?.tempPhotoKey) && attachPhoto,
            saved: {
              commonName: payload.commonName,
              scientificName: payload.scientificName ?? null,
              category,
            },
          });
        }
        // Embedded, the host flow finishes the step in onSuccess; closing here as well
        // would run its cancel path (e.g. discarding the identification photo) after a save.
        if (!embedded) onClose();
      } catch {
        // Never surface raw backend text — always the translated copy.
        setErrors(prev => ({
          ...prev,
          general: copy?.saveError ?? t('livestock.form.saveFailed'),
        }));
      } finally {
        submittingRef.current = false;
        setFinishing(false);
      }
    },
    [form, isEdit, record, createMutation, updateMutation, onSuccess, onClose, t, prefill, attachPhoto, copy, embedded],
  );

  const title = copy?.title ?? (isEdit ? t('livestock.form.editTitle') : t('livestock.form.addTitle'));

  const formElement = (
      <form
        onSubmit={handleSubmit}
        noValidate
        className="livestock-form"
        data-testid={embedded ? 'livestock-form-embedded' : undefined}
      >
        {copy?.intro}
        {errors.general && <Alert>{errors.general}</Alert>}

        <Input
          ref={firstFieldRef}
          label={t('livestock.form.commonName')}
          id="livestock-common-name"
          value={form.commonName}
          onChange={handleChange('commonName')}
          placeholder={t('livestock.form.commonNamePlaceholder')}
          disabled={isSubmitting}
          error={errors.commonName}
          required
        />

        <div className="livestock-form__lookup">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void runLookup(form.commonName, form.category)}
            isLoading={lookup.status === 'loading'}
            disabled={isSubmitting || lookup.status === 'loading' || form.commonName.trim().length < 2}
          >
            {t('livestock.lookup.action')}
          </Button>
          {lookup.status === 'loading' && (
            <LiveRegion busy>
              <Text size="sm" color="muted">{t('livestock.lookup.loading')}</Text>
            </LiveRegion>
          )}
          {lookup.status === 'found' && (
            <Alert tone="info" compact live="polite" data-testid="livestock-lookup-result">
              {t('livestock.lookup.found', {
                summary: [
                  [lookup.match.scientificName, lookup.match.tradeName ? `(${lookup.match.tradeName})` : null].filter(Boolean).join(' ') ||
                    lookup.match.commonName ||
                    form.commonName,
                  lookup.match.category ? t(`livestock.categories.${lookup.match.category}`) : '',
                  careSummary(lookup.match, t),
                ]
                  .filter(Boolean)
                  .join(' · '),
              })}
              {lookup.match.confidence !== 'high' && ` ${String(t(`livestock.lookup.confidence.${lookup.match.confidence}`))}`}
            </Alert>
          )}
          {lookup.status === 'none' && <Alert tone="info" compact live="polite">{t('livestock.lookup.none')}</Alert>}
          {lookup.status === 'error' &&
            (getAiLimitErrorKind(lookup.error) ? (
              <AiLimitErrorMessage error={lookup.error} />
            ) : (
              <Alert tone="warning" compact live="polite">{t('livestock.lookup.error')}</Alert>
            ))}
        </div>

        <div className="livestock-form__row">
          <Select
            label={t('livestock.form.category')}
            id="livestock-category"
            value={form.category}
            onChange={handleChange('category')}
            disabled={isSubmitting}
            error={errors.category}
            required
          >
            <option value="">{t('livestock.form.categoryPlaceholder')}</option>
            <option value="coral">{t('livestock.categories.coral')}</option>
            <option value="fish">{t('livestock.categories.fish')}</option>
            <option value="invertebrate">{t('livestock.categories.invertebrate')}</option>
            <option value="other">{t('livestock.categories.other')}</option>
          </Select>

          <Input
            label={t('livestock.form.dateAdded')}
            id="livestock-date-added"
            type="date"
            value={form.dateAdded}
            onChange={handleChange('dateAdded')}
            disabled={isSubmitting}
            error={errors.dateAdded}
            required
          />
        </div>

        {/* Reef Guard #1825: soft maturity notices when adding coral / fish (never block saving). */}
        {!isEdit && <LivestockMaturityNotice aquariumId={aquariumId} category={form.category} />}

        <div className="livestock-form__row">
          <Select
            label={t('livestock.form.status')}
            id="livestock-status"
            value={form.status}
            onChange={handleChange('status')}
            disabled={isSubmitting}
            error={errors.status}
            required
          >
            <option value="">{t('livestock.form.statusPlaceholder')}</option>
            <option value="active">{t('livestock.statuses.active')}</option>
            <option value="archived">{t('livestock.statuses.archived')}</option>
            <option value="deceased">{t('livestock.statuses.deceased')}</option>
            <option value="quarantine">{t('livestock.statuses.quarantine')}</option>
          </Select>

          <Input
            label={t('livestock.form.scientificName')}
            id="livestock-scientific-name"
            value={form.scientificName}
            onChange={handleChange('scientificName')}
            placeholder={t('livestock.form.scientificNamePlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        {isEdit && form.status !== 'deceased' && (
          <div className="livestock-form__field">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="livestock-form__mark-deceased"
              disabled={isSubmitting}
              onClick={() => {
                setForm(prev => ({ ...prev, status: 'deceased', deceasedAt: prev.deceasedAt || localToday() }));
                setErrors(prev => ({ ...prev, status: undefined }));
                requestAnimationFrame(() => deceasedDateRef.current?.focus());
              }}
            >
              {t('livestock.deceased.markAction')}
            </Button>
          </div>
        )}

        {form.status === 'deceased' && (
          <div className="livestock-form__row" data-testid="livestock-form-deceased">
            <Input
              ref={deceasedDateRef}
              label={t('livestock.deceased.date')}
              id="livestock-deceased-at"
              type="date"
              value={form.deceasedAt}
              min={form.dateAdded || undefined}
              max={localToday()}
              onChange={handleChange('deceasedAt')}
              disabled={isSubmitting}
              error={errors.deceasedAt}
            />
            <Input
              label={t('livestock.deceased.reason')}
              id="livestock-deceased-reason"
              value={form.deceasedReason}
              onChange={handleChange('deceasedReason')}
              placeholder={t('livestock.deceased.reasonPlaceholder')}
              maxLength={1000}
              disabled={isSubmitting}
            />
          </div>
        )}

        <Heading level={3} className="livestock-form__section-heading">{t('livestock.form.sectionPurchase')}</Heading>

        <div className="livestock-form__row">
          <Input
            label={t('livestock.form.purchasedFrom')}
            id="livestock-purchased-from"
            value={form.purchasedFrom}
            onChange={handleChange('purchasedFrom')}
            placeholder={t('livestock.form.purchasedFromPlaceholder')}
            disabled={isSubmitting}
          />

          <Input
            label={t('livestock.form.purchaseDate')}
            id="livestock-purchase-date"
            type="date"
            value={form.purchaseDate}
            onChange={handleChange('purchaseDate')}
            disabled={isSubmitting}
          />
        </div>

        <div className="livestock-form__row">
          <Input
            label={t('livestock.form.purchasePrice')}
            id="livestock-purchase-price"
            type="number"
            inputClassName="livestock-form__price-input"
            value={form.purchasePrice}
            onChange={handleChange('purchasePrice')}
            placeholder="0.00"
            disabled={isSubmitting}
            error={errors.purchasePrice}
          />

          <Input
            label={t('livestock.form.citesNumber')}
            id="livestock-cites-number"
            value={form.citesNumber}
            onChange={handleChange('citesNumber')}
            placeholder={t('livestock.form.citesNumberPlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        <div className="livestock-form__field">
          <Textarea
            id="livestock-notes"
            label={t('livestock.form.notes')}
            value={form.notes}
            onChange={handleChange('notes')}
            placeholder={t('livestock.form.notesPlaceholder')}
            disabled={isSubmitting}
            rows={3}
          />
        </div>

        {prefill?.tempPhotoKey && (
          <Stack gap="1" className="livestock-form__field">
            <Checkbox
              id="livestock-attach-photo"
              label={copy?.attachPhotoLabel ?? t('livestock.form.attachPhoto')}
              checked={attachPhoto}
              onChange={(e) => setAttachPhoto(e.target.checked)}
              disabled={isSubmitting}
              aria-describedby={copy?.attachPhotoHint ? 'livestock-attach-photo-hint' : undefined}
            />
            {copy?.attachPhotoHint && (
              <Text id="livestock-attach-photo-hint" color="muted">
                {copy.attachPhotoHint}
              </Text>
            )}
          </Stack>
        )}

        {record ? (
          <LivestockPhotosField
            key={record.id}
            aquariumId={aquariumId}
            livestockId={record.id}
            imageIds={record.imageIds}
            animalName={record.commonName}
            disabled={isSubmitting}
          />
        ) : (
          !prefill?.tempPhotoKey && (
            <Text color="muted" size="sm" className="livestock-form__field">
              {t('livestock.photos.addAfterSave')}
            </Text>
          )
        )}

        <div className="livestock-form__actions">
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            {copy?.cancelLabel ?? t('livestock.form.cancel')}
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            {copy?.submitLabel ?? t('livestock.form.save')}
          </Button>
        </div>
      </form>
  );

  if (embedded) {
    return isOpen ? formElement : null;
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={title} data-testid="livestock-form-dialog">
      {formElement}
    </Dialog>
  );
}
